import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, Icon, Message } from '@b-design/fusion';
import Button from '@/components/Button';
import CommonHeader from '@/components/CommonHeader';
import Confirm from '@/components/Confirm';
import $i18n from '../../../i18n';
import Information from './component/information';
import Versions from './component/versions';
import Members from './component/members';
import JsApiFeatures from './component/jsApiFeatures';
import Settings from './component/settings';
import Requests from '@/services/request';
import { getQueryStringArgs } from '@/utils/url-parse';
import { formatTime } from '@/utils';
import store from '@/store';
import { getChannelList } from '@/services/jsapiAuth';
import MiniappsRedirectURL from './component/MiniappsRedirectURL';

import './index.less';

const Detail: React.FC = () => {
  const [currentTabKey, setCurrentTabKey] = useState('Versions');
  const [detailData, setDetailData] = useState<any>({});
  const [createTitle, setCreateTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const { appId, spaceId } = getQueryStringArgs();
  const [userState] = store.useModel('user');
  const [spaceRoleState] = store.useModel('spaceRole');
  const [appRole, setAppRole] = useState<string[]>([]);
  const [openJsApiAuthChannel, setOpenJsApiAuthChannel] = useState<
    Array<{ innerCode: string; name: string }>
  >([]);
  const [openApply, setOpenApply] = useState(false);

  const titleList = useMemo(() => {
    return [
      {
        title: $i18n.get({ id: 'aopf.pages.miniapp.detail.Information', dm: '信息' }),
        key: 'Information',
      },
      {
        title: $i18n.get({ id: 'aopf.pages.miniapp.detail.Versions', dm: '版本' }),
        key: 'Versions',
      },
      openJsApiAuthChannel?.length > 0 &&
        detailData?.framework === 'WindVane' && {
          title: $i18n.get({ id: 'aopf.pages.miniapp.detail.tab.jsapiFeaturs', dm: 'JSAPI 功能' }),
          key: 'JsApiFeatures',
        },
      detailData?.framework === 'WindVane' && {
        title: $i18n.get({ id: 'aopf.miniapp.detail.MiniProgramJumpUrl', dm: '小程序跳转 URL' }),
        key: 'MiniappsRedirectURL',
      },
      {
        title: $i18n.get({ id: 'aopf.pages.miniapp.detail.Members', dm: '成员' }),
        key: 'Members',
      },
      {
        title: $i18n.get({ id: 'aopf.pages.miniapp.detail.Global', dm: '全局设置' }),
        key: 'Settings',
      },
    ].filter((item) => !!item);
  }, [openJsApiAuthChannel, detailData]);

  const breadcrumbConfig = [
    {
      key: 'miniapp',
      breadcrumbName: $i18n.get({ id: 'aopf.pages.miniapp.ApplicationList', dm: '应用列表' }),
      path: `/miniapp`,
    },
    {
      key: 'details',
      breadcrumbName: $i18n.get({ id: 'aopf.pages.miniapp.ApplicationDetails', dm: '应用详情' }),
    },
  ];

  const getAppRole = () => {
    Requests.get('/rest/intercnn/app/auth/query', {
      authId: appId,
      accountId: userState?.currentUser?.accountId,
    }).then((data) => {
      if (data) {
        const role = data.list[0]?.role;
        setAppRole(role ? [role] : []);
      }
    });
  };

  useEffect(() => {
    getAppRole();
  }, []);

  //TODO 查询当前空间下是否有开启jsapi管控的的渠道
  useEffect(() => {
    if (spaceRoleState.type) {
      getChannelList({
        spaceId,
        belongSpaceId: spaceRoleState?.belongSpaceId || '',
      }).then((res) => {
        if (res) {
          const openJsApiAuthList = res?.list?.filter(
            (item) => item.inuseStatus === 'ACTIVE' && item.jsapiEnable === 1,
          );
          setOpenJsApiAuthChannel(openJsApiAuthList);
        }
      });
    }
  }, [spaceRoleState]);

  const init = () => {
    setLoading(true);
    Requests.get('/rest/intercnn/app/query', {
      appIdList: appId,
      spaceId,
      currentPage: 1,
      pageSize: 10,
    }).then((data) => {
      if (data) {
        const temp = data.list[0];
        const obj = {
          name: temp.description.name,
          appId: temp.appId,
          createName: temp.description.createName,
          gmtCreate: formatTime(temp.gmtCreate),
          framework: temp.framework,
          customerPhone: temp.description.customerPhone,
          customerEmail: temp.description.customerEmail,
          icon: temp.description.iconPublicUrl,
          iconRelative: temp.description.icon,
          slogan: temp.description.slogan,
          description: temp.description.description,
          party: temp.description.party,
        };
        setDetailData(obj);
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    init();
    setCurrentTabKey('Versions');
  }, []);

  useEffect(() => {
    if (currentTabKey === 'Information') {
      init();
    }
  }, [currentTabKey]);

  useEffect(() => {
    if (createTitle === 'updateMiniapp') {
      init();
      setCreateTitle('');
    }
  }, [createTitle]);

  const delApp = async () => {
    const data = await Requests.post('/rest/intercnn/app/delete', { appId });
    if (data) {
      Message.success({
        title: $i18n.get({ id: 'aopf.pages.miniapp.detail.btn.del.msg', dm: '删除成功' }),
      });
      window.history.back();
    }
  }
  const changeTabs = (key) => {
    setCurrentTabKey(key);
  };

  useEffect(() => {
    const handler = (e) => {
      changeTabs(e.detail.type);
      setOpenApply(true);
    };
    document.addEventListener('toApplyMiniappDetailsTab', handler);
    return () => {
      document.removeEventListener('toApplyMiniappDetailsTab', handler);
    };
  }, []);

  const renderContent = (item) => {
    switch (item.key) {
      case 'Information':
        return (
          <Information
            info={detailData}
            setCreateTitle={setCreateTitle}
            loading={loading}
            setLoading={setLoading}
            currentTabKey={currentTabKey}
            appRole={appRole}
          />
        );

      case 'Versions':
        return <Versions currentTabKey={currentTabKey} info={detailData} />;
      case 'Settings':
        return <Settings info={detailData} />;
      case 'JsApiFeatures':
        return (
          <JsApiFeatures
            currentTabKey={currentTabKey}
            accountId={userState?.currentUser?.accountId}
            appId={appId}
            spaceId={spaceId || ''}
            openJsApiAuthChannels={openJsApiAuthChannel}
            openApply={openApply}
            setOpenApply={setOpenApply}
          />
        );

      case 'MiniappsRedirectURL':
        return (
          <MiniappsRedirectURL
            currentTabKey={currentTabKey}
            openApply={openApply}
            setOpenApply={setOpenApply}
          />
        );

      default:
        return <Members appRole={appRole} />;
    }
  };

  return (
    <div className="miniapp-detail">
      <CommonHeader
        title={detailData.name}
        backTo={`/miniapp?spaceId=${spaceId || ''}`}
        breadcrumbs={breadcrumbConfig}
        icon={detailData?.icon}
      />

      {/* <h1 className="detail-header">
         <Icon type="arrow--left" size="xl" onClick={() => history?.push('/miniapp')} />
         <img src={detailSource.url} className="detail-app-icon" alt="" />
         {detailSource.name}
        </h1> */}
      {currentTabKey === 'Information' && appRole?.includes('ADMIN') ? (
        <div className="btn-group">
          <Confirm
            trigger={
              <Button type="normal" ghost="light" style={{ marginLeft: 16 }}>
                <Icon type="trash-can--outline" />
                {$i18n.get({ id: 'aopf.pages.miniapp.detail.info.btn.Delete', dm: '删除' })}
              </Button>
            }
            dialogTitle={$i18n.get({
              id: 'aopf.pages.miniapp.detail.info.modal',
              dm: '确认删除应用?',
            })}
            dialogContent={$i18n.get({
              id: 'aopf.pages.miniapp.detail.info.modal.content',
              dm: '删除后所有数据将无法恢复',
            })}
            onClick={delApp}
          />
        </div>
      ) : null}

      <div className="detail-container-div">
        <Tabs
          shape="wrapped"
          onChange={changeTabs}
          activeKey={currentTabKey}
          defaultActiveKey="Versions"
        >
          {titleList.map((item) => (
            <Tabs.Item title={item.title} key={item.key}>
              {renderContent(item)}
            </Tabs.Item>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default Detail;
